// Naive-UTC date contract shared with bff-ops: `YYYY-MM-DDTHH:mm:ss`
// (optionally with fractional seconds), no zone suffix, always meaning UTC.
// Ported from the mobile app's axios.util.ts — the regex must stay identical
// or status/config payloads silently stop round-tripping.
const NAIVE_UTC_DATE = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d*)?$/

// Some bff-ops fields are plain Go `*time.Time` (e.g. TrackingObject.mountedAt),
// which marshals as RFC3339 *with* a zone — `...Z` or `±hh:mm` — not the naive
// contract. Revive those too, parsed by `new Date` (it honours the zone), so
// they reach the UI as Date objects instead of strings that crash Intl.format.
const ZONED_ISO_DATE = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?(?:Z|[+-]\d{2}:\d{2})$/

export function isNaiveUtcString(value: unknown): value is string {
  return typeof value === 'string' && NAIVE_UTC_DATE.test(value)
}

export function isZonedIsoString(value: unknown): value is string {
  return typeof value === 'string' && ZONED_ISO_DATE.test(value)
}

export function toNaiveUtc(date: Date): string {
  return date.toISOString().slice(0, 19)
}

export function parseNaiveUtc(value: string): Date {
  // Without a suffix `new Date()` parses the string as *local* time;
  // the contract says UTC, so pin it explicitly.
  return new Date(`${value}Z`)
}

export function serializeDates(data: unknown): unknown {
  if (data instanceof Date) {
    return toNaiveUtc(data)
  }
  if (Array.isArray(data)) {
    return data.map(serializeDates)
  }
  if (typeof data === 'object' && data !== null) {
    return Object.fromEntries(Object.entries(data).map(([key, value]) => [key, serializeDates(value)]))
  }
  return data
}

export function reviveDates(data: unknown): unknown {
  if (isNaiveUtcString(data)) {
    return parseNaiveUtc(data)
  }
  if (isZonedIsoString(data)) {
    return new Date(data)
  }
  if (Array.isArray(data)) {
    return data.map(reviveDates)
  }
  if (typeof data === 'object' && data !== null) {
    return Object.fromEntries(Object.entries(data).map(([key, value]) => [key, reviveDates(value)]))
  }
  return data
}

export function serializeParams(params: Record<string, unknown>): string {
  const urlParams = new URLSearchParams()
  Object.entries(params).forEach(([key, value]) => {
    if (value === null || value === undefined) {
      return
    }
    urlParams.append(key, value instanceof Date ? toNaiveUtc(value) : String(value))
  })

  return urlParams.toString()
}
